// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import MUIAlert from '../shared/mui-alert';
import LicenseRequirementAlert from './license-requirement-alert';
import { useProcess } from '../hooks';
import { Member, CurrentMember } from './add-member-modal';
import { SelectedPermissions } from '../shared/permissions';
import { fetchPricing } from '../api/plans-private';
import { createMembers } from '../api/memberships-private';
import calculateLicenseRequirements from './utils/license-calculations';
import {
  formatMembers,
  getDuplicates,
  formatCurrency,
  removeDuplicates,
  getDuplicatesByManaged,
} from '../utils/methods';
import Modal from '../components/modals/modal';
import css from '../shared/css';

interface Props {
  show: boolean;
  width: number;
  members: Member[];
  comment: string;
  positive: string;
  confirmed: () => void;
  cancelled: () => void;
  member: CurrentMember;
  orgMembers: Member[];
  permissions: SelectedPermissions;
  removeExisting: (existing: string[]) => void;
  availableLicenses: number;
  minimumLicensePurchase?: number;
  billableCount: number;
  includedUsers: number;
}

interface Duplicates {
  org: string[];
  row: string[];
}

const AddMemberSummary: React.FC<Props> = ({
  member,
  members,
  comment,
  confirmed,
  cancelled,
  orgMembers,
  permissions,
  removeExisting,
  availableLicenses,
  minimumLicensePurchase,
  billableCount,
  includedUsers,
  ...props
}: Props) => {
  const didMountRef = useRef(false);
  const { status, dispatch } = useProcess();
  const [hasError, setHasError] = useState(false);
  const [includedMembers, setIncludedMembers] = useState(members);
  const [creditMessage, setCreditMessage] = useState(null);
  const [licenseAgreementChecked, setLicenseAgreementChecked] = useState(false);
  const [costCalculated, setCostCalculated] = useState(false);
  const [removedMembers, setRemovedMembers] = useState<Duplicates>({
    org: [],
    row: [],
  });
  const [costData, setCostData] = useState({
    due_now: '$0.00',
    credit_used: '$0.00',
  });

  const { existingMembers, isManaged }: CurrentMember = member;

  useEffect(() => {
    const duplicates: Duplicates = {
      org: [],
      row: [],
    };

    formatMembers(members);
    const memberEmails = members.map((m) => m.email);

    duplicates.row = [...new Set(getDuplicates(memberEmails))];
    duplicates.org = getDuplicatesByManaged([...memberEmails, ...existingMembers], orgMembers, isManaged);

    const hasDuplicates = !!duplicates.row.length || !!duplicates.org.length;

    if (hasDuplicates) {
      setHasError(true);
      setRemovedMembers(duplicates);
      removeExisting([...duplicates.row, ...duplicates.org]);
    }

    const updatedMembers: Member[] = removeDuplicates(
      [...duplicates.row, ...duplicates.org],
      members,
    );

    setIncludedMembers(updatedMembers);
    fetchCost(updatedMembers);
  }, []);

  useEffect(() => {
    if (costData.credit_used !== '$0.00') {
      setCreditMessage(
        <span>
          A credit of
          {' '}
          <strong>{costData.credit_used}</strong>
          {' '}
          was applied to your purchase.
        </span>,
      );
    }
    // sets the page loaded after the cost calculation updates
    if (didMountRef.current) {
      dispatch({ type: 'LOADED' });
    } else {
      didMountRef.current = true;
    }
  }, [costData]);

  async function fetchCost(membersToUse = includedMembers) {
    // Filter new members for billable emails only
    const newBillableMembers = membersToUse.filter(
      (m) => !/(@spatialnetworks.com$)|(@fulcrumapp.com$)/.test(m.email),
    );

    // Calculate how many licenses need to be purchased based on minimum requirements
    const { licensesToPurchase } = calculateLicenseRequirements(
      newBillableMembers,
      availableLicenses,
      minimumLicensePurchase,
    );

    // Calculate current total licenses: available + utilized
    const licensesUtilized = Math.max(billableCount - includedUsers, 0);
    const currentLicenses = availableLicenses + licensesUtilized;

    // Desired total licenses = current + licenses to purchase
    const desiredTotalLicenses = currentLicenses + licensesToPurchase;

    const result = await fetchPricing(desiredTotalLicenses);

    if (result) {
      let dueNow = 0;
      let creditUsed = 0;

      // Check if pricing is from stripe or fulcrum
      if (isPricingResultFromStripe(result)) {
        // Upcoming Stripe::Invoice
        const invoiceIsDueNow = result.period_end < Date.now() / 1000;
        dueNow = invoiceIsDueNow ? result.amount_due : 0;
        creditUsed = invoiceIsDueNow ? result.total - result.amount_due : 0;
      } else {
        // Fulcrum PricingResult
        const billingInterval = result.interval;
        dueNow = result[billingInterval].upgrade_price;
        creditUsed = result[billingInterval].credit_used;
      }

      setCostData({
        due_now: formatCurrency(dueNow),
        credit_used: formatCurrency(creditUsed),
      });
      setCostCalculated(true);
    } else {
      setCostCalculated(true);
      dispatch({ type: 'LOADED' });
    }
  }

  function isPricingResultFromStripe(result) {
    return result.object === 'invoice' && result.billing_reason === 'upcoming';
  }

  function renderErrorBox() {
    return (
      <>
        {!!removedMembers.org.length && (
          <>
            <MUIAlert severity="warning">
              These emails were skipped because they already belong to your organization:
            </MUIAlert>
            <div className={cx(styles.container, styles.containerWarning)}>
              <ul className={styles.listStyle} style={{ maxHeight: 100 }}>
                {removedMembers.org.map((removed, idx) => (
                  // allowed since removed is just an email string here
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={idx}>{removed}</li>
                ))}
              </ul>
            </div>
          </>
        )}
        {!!removedMembers.row.length && (
          <>
            <MUIAlert severity="warning">We found & skipped these duplicates in your list:</MUIAlert>
            <div className={cx(styles.container, styles.containerWarning)}>
              <ul className={styles.listStyle} style={{ maxHeight: 100 }}>
                {removedMembers.row.map((removed, idx) => (
                  // allowed since removed is just an email string here
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={idx}>{removed}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </>
    );
  }

  async function handleConfirmed() {
    dispatch({ type: 'STARTED' });

    const body = {
      memberships: includedMembers,
      permissions: {
        forms: permissions.selectedForms,
        layers: permissions.selectedLayers,
        projects: permissions.selectedProjects,
        groups: permissions.selectedGroups,
      },
      managed: isManaged,
      message: comment,
    };

    await createMembers(body, (res: {ok: boolean}) => {
      dispatch({ type: 'SUCCESS' });
      if (res.ok) {
        confirmed();
      }
    });
  }

  const handleLicenseAgreementChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLicenseAgreementChecked(event.target.checked);
  };

  const renderCost = (costCalculated: boolean, costData: { due_now: string }) => {
    if (costCalculated) {
      return <strong>{costData.due_now}</strong>;
    }

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <CircularProgress
          size={14}
          color="inherit"
        />
        <strong> Calculating total</strong>
      </Box>
    );
  };

  return (
    <Modal
      open={props.show}
      onClose={cancelled}
      title="Summary of Pending Changes"
      positive={props.positive}
      negative="Cancel"
      slideTop
      legacyMode
      disabled={status === 'pending'}
      positiveDisabled={!includedMembers.length || status === 'initial load' || !licenseAgreementChecked}
      onPositive={() => handleConfirmed()}
      onNegative={() => cancelled()}
    >
      {includedMembers.length ? (
        <>
          {hasError && renderErrorBox()}
          <LicenseRequirementAlert
            members={includedMembers}
            availableLicenses={availableLicenses}
            minimumLicensePurchase={minimumLicensePurchase}
          />
          <MUIAlert severity="info">The following users will be added to your organization:</MUIAlert>
          <div className={cx(styles.container, styles.containerSuccess)}>
            <ul className={styles.listStyle}>
              {includedMembers.map((included: Member) => (
                <li key={included._id}>
                  {included.first_name}
                  {' '}
                  {included.last_name}
                  {' '}
                  -
                  {' '}
                  {included.email.trim()}
                </li>
              ))}
            </ul>
          </div>
          <FormGroup>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={licenseAgreementChecked}
                  onChange={handleLicenseAgreementChecked}
                  inputProps={{ 'aria-label': 'License agreement checkbox' }}
                  size="small"
                  sx={{
                    '&.Mui-checked': {
                      color: '#4baef8',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                      display: 'none',
                    },
                    alignSelf: 'flex-start',
                    paddingTop: '.125rem',
                  }}
                  data-testid="license-agreement-checkbox"
                />
              )}
              label="I understand that if additional licenses are required, they will be automatically purchased and co-termed with my current contract."
              labelPlacement="end"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                },
              }}
            />
          </FormGroup>
          <span className={styles.cost} style={{ opacity: 1 }}>
            <p>
              {costData.credit_used !== '$0.00' && creditMessage}
              {' '}
              You will be billed:
              {' '}
              {renderCost(costCalculated, costData)}
            </p>
          </span>
        </>
      ) : (
        <>
          {hasError && renderErrorBox()}
        </>
      )}
    </Modal>
  );
};

const styles = css({
  container: {
    background: '#f8f8f8',
    border: '1px solid #eee',
    borderRadius: 5,
    margin: '15px 0',
  },
  containerWarning: {
    background: '#fff',
    border: '1px solid #ffcc92',
  },
  containerSuccess: {
    border: '1px solid #4baef8',
  },
  title: {
    margin: '10px 0',
    fontWeight: 700,
    fontSize: 17,
    color: '#333',
  },
  listStyle: {
    maxHeight: 250,
    fontSize: 16,
    overflow: 'auto',
    margin: 0,
    padding: '10px 15px',
    listStyle: 'inside',
  },
  padding: {
    padding: '10px 15px',
  },
  cost: {
    transition: '.15s',
    margin: '15px 0',
    display: 'block',
    minHeight: 21,
  },
});

export default AddMemberSummary;
